import React, { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from "@dnd-kit/core";
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  useSortable,
  SortableContext as SortableContextType,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/button";
import { Badge } from "@/components/badge";
import { Input } from "@/components/input";
import { Field, Label, ErrorMessage } from "@/components/fieldset";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/dropdown";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  Bars3Icon,
  EllipsisVerticalIcon,
} from "@heroicons/react/20/solid";
import {
  useCreateDomain,
  useUpdateDomain,
  useDeleteDomain,
} from "@/apis/domains";
import {
  useQueryGetCategoriesByDomain,
  useQueryGetCategoriesWithoutDomain,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  apiUpdateCategory,
  apiBulkUpdateCategoryOrder,
} from "@/apis/categories";
import apiClient from "@/apis/client";
import { useQueryClient } from "@tanstack/react-query";
import type { IDomain } from "@/apis/domains/types";
import type { ICategory } from "@/apis/categories/types";
import type { IExamConfigDomain } from "@/apis/exam_configs/getExamConfigList";
import { message } from "antd";

interface SubjectDomainManagerProps {
  examType: string;
  subject: string;
  domains: IExamConfigDomain[]; // ExamConfig에서 전달받은 도메인 목록
}

interface DomainFormData {
  name: string;
}

interface CategoryFormData {
  name: string;
  short_name: string;
}

// 드래그 가능한 도메인 컴포넌트
const SortableDomain: React.FC<{
  domain: IExamConfigDomain;
  examType: string;
  subject: string;
  isEditing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCancelEdit: () => void;
  onSave: (data: DomainFormData) => void;
  formData: DomainFormData;
  onFormChange: (data: DomainFormData) => void;
  errors: Record<string, string>;
  onEditCategory: (category: ICategory) => void;
  onDeleteCategory: (categoryId: string) => void;
  onRemoveCategoryFromDomain: (categoryId: string) => void;
}> = ({
  domain,
  examType,
  subject,
  isEditing,
  onEdit,
  onDelete,
  onCancelEdit,
  onSave,
  formData,
  onFormChange,
  errors,
  onEditCategory,
  onDeleteCategory,
  onRemoveCategoryFromDomain,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: domain.id,
    data: { type: "domain", domain },
  });

  // ref 설정
  const setRefs = (element: HTMLElement | null) => {
    setNodeRef(element);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // 도메인별 카테고리 조회
  const { data: categoriesData } = useQueryGetCategoriesByDomain(domain.id);
  const categories = (categoriesData?.data || []).sort(
    (a: ICategory, b: ICategory) => a.order - b.order
  );

  return (
    <div
      ref={setRefs}
      style={style}
      className="bg-white border border-gray-200 rounded-lg shadow-sm"
    >
      {/* 도메인 헤더 */}
      <div className="p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              {...attributes}
              {...listeners}
              className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600"
            >
              <Bars3Icon className="w-5 h-5" />
            </button>
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={formData.name}
                  onChange={(e) =>
                    onFormChange({ ...formData, name: e.target.value })
                  }
                  placeholder="Domain name"
                  className="w-48"
                  aria-invalid={!!errors.name}
                />
                <Button onClick={() => onSave(formData)}>
                  <CheckIcon className="w-4 h-4" />
                </Button>
                <Button onClick={onCancelEdit} plain>
                  <XMarkIcon className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <h4 className="text-lg font-medium text-gray-900">
                  {domain.name}
                </h4>
                <Badge color="blue">Order: {domain.order}</Badge>
              </>
            )}
          </div>
          {!isEditing && (
            <div className="flex gap-1">
              <Button onClick={onEdit}>
                <PencilIcon className="w-4 h-4" />
              </Button>
              <Button onClick={onDelete} color="red">
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
        {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
      </div>

      {/* 카테고리 드롭 영역 */}
      <div className="p-4 min-h-[100px]">
        <div className="text-sm text-gray-600 mb-3">
          Categories ({categories.length})
          <span className="text-xs text-gray-400 ml-2">
            • Drag to reorder within domain
          </span>
        </div>
        <SortableContext
          items={categories.map((cat) => cat.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {categories.map((category) => (
              <SortableCategory
                key={category.id}
                category={category}
                examType={examType}
                subject={subject}
                onEdit={onEditCategory}
                onDelete={onDeleteCategory}
                onRemoveFromDomain={onRemoveCategoryFromDomain}
              />
            ))}
          </div>
        </SortableContext>
        {categories.length === 0 && (
          <div className="text-center py-6 text-gray-400 border-2 border-dashed border-gray-200 rounded-md">
            <div className="text-sm">No categories in this domain</div>
            <div className="text-xs mt-1">
              Drag categories here to organize them
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 드래그 가능한 카테고리 컴포넌트
const SortableCategory: React.FC<{
  category: ICategory;
  examType: string;
  subject: string;
  onEdit?: (category: ICategory) => void;
  onDelete?: (categoryId: string) => void;
  onRemoveFromDomain?: (categoryId: string) => void;
}> = ({
  category,
  examType,
  subject,
  onEdit,
  onDelete,
  onRemoveFromDomain,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: category.id,
    data: { type: "category", category },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleEdit = () => {
    onEdit?.(category);
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${category.name}"?`)) {
      onDelete?.(category.id);
    }
  };

  const handleRemoveFromDomain = () => {
    if (window.confirm(`Remove "${category.name}" from its domain?`)) {
      onRemoveFromDomain?.(category.id);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:shadow-sm"
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600"
        >
          <Bars3Icon className="w-4 h-4" />
        </button>
        <div>
          <div className="font-medium text-sm">{category.name}</div>
          <div className="text-xs text-gray-500">
            Short: {category.short_name} • Order: {category.order}
          </div>
        </div>
        <Badge color="green">Active</Badge>
      </div>

      <div className="flex items-center gap-2">
        <Dropdown>
          <DropdownButton as={Button} plain>
            <EllipsisVerticalIcon className="w-4 h-4" />
          </DropdownButton>
          <DropdownMenu anchor="bottom end">
            <DropdownItem onClick={handleEdit}>
              <PencilIcon className="w-4 h-4" />
              <DropdownLabel>Edit</DropdownLabel>
            </DropdownItem>
            <DropdownItem onClick={handleDelete}>
              <TrashIcon className="w-4 h-4" />
              <DropdownLabel>Delete</DropdownLabel>
            </DropdownItem>
            {category.domain_id && (
              <DropdownItem onClick={handleRemoveFromDomain}>
                <XMarkIcon className="w-4 h-4" />
                <DropdownLabel>Remove from Domain</DropdownLabel>
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>
    </div>
  );
};

// "Categories Without Domain" 영역 컴포넌트
const CategoriesWithoutDomain: React.FC<{
  categoriesWithoutDomain: ICategory[];
  examType: string;
  subject: string;
  onEditCategory: (category: ICategory) => void;
  onDeleteCategory: (categoryId: string) => void;
}> = ({
  categoriesWithoutDomain,
  examType,
  subject,
  onEditCategory,
  onDeleteCategory,
}) => {
  return (
    <div className="space-y-4">
      <h6 className="text-md font-medium text-yellow-800">
        Categories Without Domain ({categoriesWithoutDomain.length})
        <span className="text-xs text-yellow-600 ml-2">• Drag to reorder</span>
      </h6>

      {categoriesWithoutDomain.length > 0 ? (
        <SortableContext
          items={categoriesWithoutDomain.map((cat) => cat.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {categoriesWithoutDomain
              .sort((a, b) => a.order - b.order)
              .map((category) => (
                <SortableCategory
                  key={category.id}
                  category={category}
                  examType={examType}
                  subject={subject}
                  onEdit={onEditCategory}
                  onDelete={onDeleteCategory}
                  // 독립 카테고리는 도메인에서 제거할 수 없음
                />
              ))}
          </div>
        </SortableContext>
      ) : (
        <div className="text-center py-8 text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="text-sm">No independent categories</div>
          <div className="text-xs mt-1">
            Categories removed from domains will appear here
          </div>
        </div>
      )}
    </div>
  );
};

const SubjectDomainManager: React.FC<SubjectDomainManagerProps> = ({
  examType,
  subject,
  domains = [], // 기본값 설정
}) => {
  const queryClient = useQueryClient();
  const [isAddingDomain, setIsAddingDomain] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [editingDomainId, setEditingDomainId] = useState<string | null>(null);
  const [domainFormData, setDomainFormData] = useState<DomainFormData>({
    name: "",
  });
  const [categoryFormData, setCategoryFormData] = useState<CategoryFormData>({
    name: "",
    short_name: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeId, setActiveId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<ICategory | null>(
    null
  );

  // 도메인 미연결 카테고리 조회
  const { data: categoriesWithoutDomainData } =
    useQueryGetCategoriesWithoutDomain(examType, subject);

  const createDomainMutation = useCreateDomain(examType, subject);
  const updateDomainMutation = useUpdateDomain(
    examType,
    subject,
    editingDomainId || ""
  );
  const deleteDomainMutation = useDeleteDomain(examType, subject);
  const createCategoryMutation = useCreateCategory();
  const deleteCategoryMutation = useDeleteCategory();

  const categoriesWithoutDomain = categoriesWithoutDomainData?.data || [];

  // 드래그 센서 설정
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // 드래그 시작
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  // 드래그 오버
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // 카테고리를 도메인으로 드래그할 때 시각적 피드백
    if (activeData?.type === "category" && overData?.type === "domain") {
      // 여기서 시각적 피드백을 추가할 수 있습니다 (예: 도메인 하이라이트)
    }
  };

  // 드래그 종료
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // 디버깅을 위한 로그
    console.log("Drag end event:", {
      activeId: active.id,
      overId: over.id,
      activeData,
      overData,
    });

    // 도메인 순서 변경
    if (activeData?.type === "domain" && overData?.type === "domain") {
      const oldIndex = domains.findIndex((d) => d.id === active.id);
      const newIndex = domains.findIndex((d) => d.id === over.id);

      if (oldIndex !== newIndex) {
        const newDomains = arrayMove(domains, oldIndex, newIndex);
        // 순서 업데이트 API 호출
        try {
          for (let i = 0; i < newDomains.length; i++) {
            if (newDomains[i].order !== i + 1) {
              await updateDomainMutation.mutateAsync({
                order: i + 1,
              });
            }
          }
          message.success("Domain order updated successfully");
        } catch (error: any) {
          message.error(error?.message || "Failed to update domain order");
        }
      }
    }

    // 카테고리 순서 변경 및 도메인 이동 처리
    if (activeData?.type === "category") {
      const category = activeData.category as ICategory;

      console.log("Category drag detected:", {
        categoryId: category.id,
        categoryName: category.name,
        categoryDomainId: category.domain_id,
        overId: over.id,
        overDataType: overData?.type,
        overData,
      });

      // 같은 도메인 내에서 카테고리 순서 변경
      if (overData?.type === "category") {
        const targetCategory = overData.category as ICategory;

        console.log("Dragging over another category:", {
          targetCategoryId: targetCategory.id,
          targetCategoryName: targetCategory.name,
          targetDomainId: targetCategory.domain_id,
          sameDomain: category.domain_id === targetCategory.domain_id,
          sameCategory: category.id === targetCategory.id,
        });

        // 같은 도메인 내에서의 순서 변경
        if (
          category.domain_id === targetCategory.domain_id &&
          category.id !== targetCategory.id
        ) {
          console.log("Executing category reorder in same domain");
          await handleCategoryReorder(
            category.domain_id,
            category.id,
            targetCategory.id
          );
          return;
        }
      }

      // 카테고리를 다른 도메인으로 이동
      let targetDomainId: string | null = null;

      // 도메인으로 드롭된 경우
      if (overData?.type === "domain") {
        targetDomainId = over.id as string;
      }
      // 도메인 밖 영역으로 드롭된 경우 (미연결 카테고리 영역 등)
      else if (
        over.id === "categories-without-domain" ||
        overData?.type === "categories-without-domain"
      ) {
        targetDomainId = null;
      }
      // 다른 카테고리 위에 드롭된 경우, 그 카테고리가 속한 도메인으로 이동
      else if (overData?.type === "category") {
        const targetCategory = overData.category as ICategory;
        targetDomainId = targetCategory.domain_id;
      }

      // 디버깅을 위한 로그
      console.log("Moving category:", {
        categoryId: category.id,
        categoryName: category.name,
        fromDomainId: category.domain_id,
        toDomainId: targetDomainId,
        overId: over.id,
        overDataType: overData?.type,
        payload: { domain_id: targetDomainId },
      });

      // 카테고리의 도메인이 실제로 변경되는 경우에만 API 호출
      if (targetDomainId !== category.domain_id) {
        try {
          // 직접 API 호출 (React Query 없이)
          await apiUpdateCategory(category.id, {
            domain_id: targetDomainId,
          });

          // 관련 쿼리 무효화
          queryClient.invalidateQueries({
            queryKey: ["categories"],
          });

          const targetName = targetDomainId
            ? domains.find((d) => d.id === targetDomainId)?.name || "domain"
            : "independent categories";
          message.success(`Category moved to ${targetName} successfully`);
        } catch (error: any) {
          console.error("Failed to move category:", error);
          message.error(error?.message || "Failed to move category");
        }
      }
    }
  };

  // 카테고리 순서 변경 처리 함수
  const handleCategoryReorder = async (
    domainId: string | null,
    draggedCategoryId: string,
    targetCategoryId: string
  ) => {
    try {
      console.log("Category reorder started:", {
        domainId,
        draggedCategoryId,
        targetCategoryId,
      });

      // 해당 도메인의 모든 카테고리 가져오기
      let currentCategories: ICategory[] = [];

      if (domainId) {
        // 캐시된 데이터에서 직접 가져오기 - 올바른 쿼리 키 사용
        const categoriesData = queryClient.getQueryData([
          "categories",
          "domain",
          domainId,
        ]) as any;
        currentCategories = categoriesData?.data || [];

        // 캐시된 데이터가 없으면 실제 API 호출
        if (currentCategories.length === 0) {
          console.log("No cached data, fetching from API...");
          const freshData = await queryClient.fetchQuery({
            queryKey: ["categories", "domain", domainId],
            queryFn: async () => {
              const response = await apiClient.get(
                `/categories/domain/${domainId}`
              );
              return response.data;
            },
          });
          currentCategories = freshData?.data || [];
        }
      } else {
        // 독립 카테고리 데이터 가져오기 - 올바른 쿼리 키 사용
        const categoriesData = queryClient.getQueryData([
          "categories",
          "without-domain",
          examType,
          subject,
        ]) as any;
        currentCategories = categoriesData?.data || [];

        // 캐시된 데이터가 없으면 실제 API 호출
        if (currentCategories.length === 0) {
          console.log(
            "No cached data for without-domain, fetching from API..."
          );
          const freshData = await queryClient.fetchQuery({
            queryKey: ["categories", "without-domain", examType, subject],
            queryFn: async () => {
              const response = await apiClient.get(
                `/categories/without-domain`,
                { params: { exam_type: examType, subject: subject } }
              );
              return response.data;
            },
          });
          currentCategories = freshData?.data || [];
        }
      }

      console.log("Current categories:", currentCategories);

      if (currentCategories.length === 0) {
        console.warn("No categories found for reordering even after API call");
        return;
      }

      // order 기준으로 정렬
      currentCategories.sort((a, b) => a.order - b.order);

      const draggedIndex = currentCategories.findIndex(
        (cat) => cat.id === draggedCategoryId
      );
      const targetIndex = currentCategories.findIndex(
        (cat) => cat.id === targetCategoryId
      );

      console.log("Indexes:", { draggedIndex, targetIndex });

      if (
        draggedIndex === -1 ||
        targetIndex === -1 ||
        draggedIndex === targetIndex
      ) {
        console.warn("Invalid indexes or same position");
        return;
      }

      // 배열 순서 변경
      const reorderedCategories = arrayMove(
        currentCategories,
        draggedIndex,
        targetIndex
      );

      console.log("Reordered categories:", reorderedCategories);

      // 새로운 order 값으로 업데이트할 카테고리 목록 생성
      const categoriesToUpdate = reorderedCategories
        .map((category, index) => {
          const newOrder = index + 1;
          if (category.order !== newOrder) {
            console.log(
              `Will update category ${category.name} order: ${category.order} -> ${newOrder}`
            );
            return { id: category.id, order: newOrder };
          }
          return null;
        })
        .filter(Boolean) as { id: string; order: number }[];

      if (categoriesToUpdate.length > 0) {
        console.log(
          "Executing bulk update for",
          categoriesToUpdate.length,
          "categories"
        );
        await apiBulkUpdateCategoryOrder(examType, subject, categoriesToUpdate);
      } else {
        console.log("No categories need order update");
      }

      // 관련 쿼리 무효화
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
      if (domainId) {
        queryClient.invalidateQueries({
          queryKey: ["categories", "domain", domainId],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ["categories", "without-domain", examType, subject],
        });
      }

      message.success("Category order updated successfully");
    } catch (error: any) {
      console.error("Failed to reorder categories:", error);
      message.error(error?.message || "Failed to reorder categories");
    }
  };

  // 도메인 관련 함수들
  const handleStartAddDomain = () => {
    setIsAddingDomain(true);
    setDomainFormData({ name: "" });
    setErrors({});
  };

  const handleCancelAddDomain = () => {
    setIsAddingDomain(false);
    setDomainFormData({ name: "" });
    setErrors({});
  };

  const handleCreateDomain = async () => {
    if (!domainFormData.name.trim()) {
      setErrors({ name: "Domain name is required" });
      return;
    }

    try {
      await createDomainMutation.mutateAsync({
        name: domainFormData.name,
        order: domains.length + 1,
      });
      message.success("Domain created successfully");
      handleCancelAddDomain();
    } catch (error: any) {
      message.error(error?.message || "Failed to create domain");
    }
  };

  const handleStartEditDomain = (domain: IExamConfigDomain) => {
    setEditingDomainId(domain.id);
    setDomainFormData({ name: domain.name });
    setErrors({});
  };

  const handleCancelEditDomain = () => {
    setEditingDomainId(null);
    setDomainFormData({ name: "" });
    setErrors({});
  };

  const handleUpdateDomain = async (data: DomainFormData) => {
    if (!data.name.trim()) {
      setErrors({ name: "Domain name is required" });
      return;
    }

    try {
      await updateDomainMutation.mutateAsync({ name: data.name });
      message.success("Domain updated successfully");
      handleCancelEditDomain();
    } catch (error: any) {
      message.error(error?.message || "Failed to update domain");
    }
  };

  const handleDeleteDomain = async (domainId: string) => {
    if (!window.confirm("Are you sure you want to delete this domain?")) {
      return;
    }

    try {
      await deleteDomainMutation.mutateAsync(domainId);
      message.success("Domain deleted successfully");
    } catch (error: any) {
      message.error(error?.message || "Failed to delete domain");
    }
  };

  // 카테고리 관련 함수들
  const handleStartAddCategory = () => {
    setIsAddingCategory(true);
    setCategoryFormData({ name: "", short_name: "" });
    setErrors({});
  };

  const handleCancelAddCategory = () => {
    setIsAddingCategory(false);
    setEditingCategory(null);
    setCategoryFormData({ name: "", short_name: "" });
    setErrors({});
  };

  const handleCreateCategory = async () => {
    const newErrors: Record<string, string> = {};
    if (!categoryFormData.name.trim()) {
      newErrors.name = "Category name is required";
    }
    if (!categoryFormData.short_name.trim()) {
      newErrors.short_name = "Short name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      if (editingCategory) {
        // 기존 카테고리 수정
        await apiUpdateCategory(editingCategory.id, {
          name: categoryFormData.name,
          short_name: categoryFormData.short_name,
        });
        message.success("Category updated successfully");
      } else {
        // 새 카테고리 생성 - order 값 계산
        const allCategoriesData = await queryClient.fetchQuery({
          queryKey: ["categories", examType, subject],
          queryFn: () =>
            queryClient.getQueryData(["categories", examType, subject]) || {
              data: [],
            },
        });
        const allCategories = (allCategoriesData as any)?.data || [];
        const maxOrder =
          allCategories.length > 0
            ? Math.max(...allCategories.map((cat: ICategory) => cat.order))
            : 0;

        await createCategoryMutation.mutateAsync({
          exam_type: examType,
          subject: subject,
          name: categoryFormData.name,
          short_name: categoryFormData.short_name,
          order: maxOrder + 1,
          is_active: true,
        });
        message.success("Category created successfully");
      }

      // 관련된 모든 쿼리 무효화
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
      queryClient.invalidateQueries({
        queryKey: ["categories-by-domain"],
      });
      queryClient.invalidateQueries({
        queryKey: ["categories-without-domain"],
      });
      queryClient.invalidateQueries({
        queryKey: ["exam-configs"],
      });

      handleCancelAddCategory();
    } catch (error: any) {
      message.error(
        error?.message ||
          `Failed to ${editingCategory ? "update" : "create"} category`
      );
    }
  };

  // 카테고리 편집 핸들러
  const handleEditCategory = (category: ICategory) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      short_name: category.short_name,
    });
    setIsAddingCategory(true);
  };

  // 카테고리 삭제 핸들러
  const handleDeleteCategory = async (categoryId: string) => {
    try {
      await deleteCategoryMutation.mutateAsync(categoryId);
      message.success("Category deleted successfully");

      // 관련된 모든 쿼리 무효화
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
      queryClient.invalidateQueries({
        queryKey: ["categories-by-domain"],
      });
      queryClient.invalidateQueries({
        queryKey: ["categories-without-domain"],
      });
      queryClient.invalidateQueries({
        queryKey: ["exam-configs"],
      });
    } catch (error: any) {
      message.error(error?.message || "Failed to delete category");
    }
  };

  // 카테고리를 도메인에서 제거하는 핸들러
  const handleRemoveCategoryFromDomain = async (categoryId: string) => {
    try {
      await apiUpdateCategory(categoryId, {
        domain_id: null,
      });
      message.success("Category removed from domain successfully");

      // 관련된 모든 쿼리 무효화
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
      queryClient.invalidateQueries({
        queryKey: ["categories-by-domain"],
      });
      queryClient.invalidateQueries({
        queryKey: ["categories-without-domain"],
      });
      queryClient.invalidateQueries({
        queryKey: ["exam-configs"],
      });
    } catch (error: any) {
      message.error(error?.message || "Failed to remove category from domain");
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-6">
        {/* 헤더 */}
        <div className="flex items-center justify-between">
          <h5 className="text-lg font-medium text-gray-700">
            Domains & Categories
          </h5>
          <div className="flex gap-2">
            {!isAddingDomain && !editingDomainId && (
              <Button onClick={handleStartAddDomain}>
                <PlusIcon className="w-4 h-4" />
                Add Domain
              </Button>
            )}
            {!isAddingCategory && (
              <Button onClick={handleStartAddCategory}>
                <PlusIcon className="w-4 h-4" />
                Add Category
              </Button>
            )}
          </div>
        </div>

        {/* 도메인 추가 폼 */}
        {isAddingDomain && (
          <div className="p-4 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
            <Field>
              <Label>Domain Name*</Label>
              <Input
                value={domainFormData.name}
                onChange={(e) =>
                  setDomainFormData({ ...domainFormData, name: e.target.value })
                }
                placeholder="e.g., Algebra"
                aria-invalid={!!errors.name}
              />
              {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
            </Field>
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={handleCancelAddDomain} plain>
                <XMarkIcon className="w-4 h-4" />
                Cancel
              </Button>
              <Button
                onClick={handleCreateDomain}
                disabled={createDomainMutation.isPending}
              >
                <CheckIcon className="w-4 h-4" />
                Create Domain
              </Button>
            </div>
          </div>
        )}

        {/* 카테고리 추가 폼 */}
        {isAddingCategory && (
          <div className="p-4 border-2 border-dashed border-gray-300 rounded-md bg-gray-50">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field>
                <Label>Category Name*</Label>
                <Input
                  value={categoryFormData.name}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      name: e.target.value,
                    })
                  }
                  placeholder="e.g., Linear Equations"
                  aria-invalid={!!errors.name}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>
              <Field>
                <Label>Short Name*</Label>
                <Input
                  value={categoryFormData.short_name}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      short_name: e.target.value,
                    })
                  }
                  placeholder="e.g., linear_eq"
                  aria-invalid={!!errors.short_name}
                />
                {errors.short_name && (
                  <ErrorMessage>{errors.short_name}</ErrorMessage>
                )}
              </Field>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button onClick={handleCancelAddCategory} plain>
                <XMarkIcon className="w-4 h-4" />
                Cancel
              </Button>
              <Button
                onClick={handleCreateCategory}
                disabled={createCategoryMutation.isPending}
              >
                <CheckIcon className="w-4 h-4" />
                {editingCategory ? "Update Category" : "Create Category"}
              </Button>
            </div>
          </div>
        )}

        {/* 도메인 목록 */}
        <SortableContext
          items={domains.map((domain) => domain.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-4">
            {domains.map((domain) => (
              <SortableDomain
                key={domain.id}
                domain={domain}
                examType={examType}
                subject={subject}
                isEditing={editingDomainId === domain.id}
                onEdit={() => handleStartEditDomain(domain)}
                onDelete={() => handleDeleteDomain(domain.id)}
                onCancelEdit={handleCancelEditDomain}
                onSave={handleUpdateDomain}
                formData={domainFormData}
                onFormChange={setDomainFormData}
                errors={errors}
                onEditCategory={handleEditCategory}
                onDeleteCategory={handleDeleteCategory}
                onRemoveCategoryFromDomain={handleRemoveCategoryFromDomain}
              />
            ))}
          </div>
        </SortableContext>

        {/* 도메인 미연결 카테고리 */}
        <div
          id="categories-without-domain"
          className="border-2 border-dashed border-yellow-200 rounded-lg p-4 bg-yellow-50"
        >
          <CategoriesWithoutDomain
            categoriesWithoutDomain={categoriesWithoutDomain}
            examType={examType}
            subject={subject}
            onEditCategory={handleEditCategory}
            onDeleteCategory={handleDeleteCategory}
          />
        </div>

        {/* 빈 상태 */}
        {domains.length === 0 && !isAddingDomain && (
          <div className="text-center py-12 text-gray-500">
            <div className="text-lg mb-2">No domains found</div>
            <div className="text-sm">
              Create domains to organize your categories effectively.
            </div>
          </div>
        )}
      </div>

      {/* 드래그 오버레이 */}
      <DragOverlay>
        {activeId ? (
          <div className="bg-white border border-gray-300 rounded-md p-3 shadow-lg opacity-90">
            Dragging...
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default SubjectDomainManager;
